import React, { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { useAppTranslation } from "../../i18n/useAppTranslation";
import CloseButton from "../../Components/CloseButton/CloseButton";
import {
  fetchWorkspaceByGuid,
  updateWorkspace,
  deleteWorkspace,
  WorkspaceResponse,
  WORKSPACE_PHASES,
} from "../../api/workspaces";
import { getIsSystemAdmin } from "../../auth/AuthContext";
import { mediaSrc } from "../../api/mediaUrl";
import { useApiWorkspaceAi } from "../../api/useApiWorkspaceAi";
import { CopilotTray } from "./CopilotTray";
import EvidenceTracker from "./EvidenceTracker";
import EvidenceCompilationPanel from "./EvidenceCompilationPanel";
import SignalDetectionPanel from "./SignalDetectionPanel";
import WorkspaceSummaryPanel from "./WorkspaceSummaryPanel";
import InvestigationTeamModal from "./InvestigationTeamModal";
import styles from "./WorkspaceDetailPage.module.css";

interface Props {
  workspaceGuid: string;
  /**
   * DEV-1452 — no longer a back BUTTON. Retained solely as the programmatic
   * exit after a soft-delete: the workspace the route points at no longer
   * exists, so the page must leave on its own.
   */
  onBack: () => void;
  currentUserGuid?: string;
}

const WorkspaceDetailPage: React.FC<Props> = ({
  workspaceGuid,
  onBack,
  currentUserGuid,
}) => {
  const { t } = useAppTranslation("pages");
  const [workspace, setWorkspace] = useState<WorkspaceResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activePhase, setActivePhase] = useState<string | null>(null);
  const [activeSubTab, setActiveSubTab] = useState<string>("collection_plan");
  const [teamModalOpen, setTeamModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editDescription, setEditDescription] = useState("");
  const [editFindings, setEditFindings] = useState("");
  const [editSaving, setEditSaving] = useState(false);
  // ── Soft-delete (danger zone) state ──
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [delText, setDelText] = useState("");
  const [deleting, setDeleting] = useState(false);

  // AI data — shared between Evidence sub-tab and CopilotTray (cache-backed, no duplicate fetches)
  const aiData = useApiWorkspaceAi(workspace?.workspaceGuid);
  const chevronScrollRef = useRef<HTMLDivElement>(null);

  const openEditModal = useCallback(() => {
    if (!workspace) return;
    setEditDescription(workspace.description ?? "");
    setEditFindings(workspace.summaryFindings ?? "");
    setEditModalOpen(true);
  }, [workspace]);

  const handleEditSave = useCallback(async () => {
    if (!workspace) return;
    setEditSaving(true);
    try {
      const updated = await updateWorkspace(workspace.workspaceGuid, {
        description: editDescription,
        summaryFindings: editFindings,
      });
      setWorkspace(updated);
      setEditModalOpen(false);
    } catch (e: any) {
      setError(e?.message ?? "Failed to save changes.");
    } finally {
      setEditSaving(false);
    }
  }, [workspace, editDescription, editFindings]);

  // Who may soft-delete: the team lead or a system admin.
  const canDelete = useMemo(
    () => !!workspace && (getIsSystemAdmin()
      || (workspace.teamLeadGuid || "").toUpperCase() === (currentUserGuid || "").toUpperCase()),
    [workspace, currentUserGuid],
  );

  const handleSoftDelete = useCallback(async () => {
    if (!workspace) return;
    setDeleting(true);
    setError(null);
    try {
      await deleteWorkspace(workspace.workspaceGuid);
      setEditModalOpen(false);
      onBack();
    } catch (e: any) {
      setError(e?.message ?? "Failed to delete workspace.");
      setDeleting(false);
    }
  }, [workspace, onBack]);

  /** Full initial load — sets workspace data AND resets the active phase */
  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchWorkspaceByGuid(workspaceGuid);
      setWorkspace(data);
      // Default to Signal Detection as the landing view (initial load only)
      setActivePhase("signal_detection");
    } catch (e: any) {
      setError(e?.message ?? "Failed to load workspace.");
    } finally {
      setLoading(false);
    }
  }, [workspaceGuid]);

  /** Light refresh — reloads workspace data without resetting the active phase/tab */
  const refreshWorkspace = useCallback(async () => {
    try {
      const data = await fetchWorkspaceByGuid(workspaceGuid);
      setWorkspace(data);
    } catch {
      // Silent — user already has stale data visible; next full load will catch errors
    }
  }, [workspaceGuid]);

  useEffect(() => {
    load();
  }, [load]);

  const phaseLabel = useCallback(
    (phase: string): string =>
      t(`workspace.phaseLabels.${phase}`) || phase,
    [t]
  );

  const currentPhaseIndex = useMemo(
    () =>
      workspace
        ? WORKSPACE_PHASES.indexOf(workspace.phase as any)
        : -1,
    [workspace]
  );

  /** Sub-tabs within Detection & Validation phase */
  const DETECTION_SUB_TABS = useMemo(
    () => [
      { key: "collection_plan", label: t("workspace.detail.collectionPlanTab") },
      { key: "evidence",        label: t("workspace.detail.evidenceTab") },
      { key: "compilation",     label: t("workspace.detail.compilationTab") },
    ],
    [t]
  );

  /** Reset sub-tab to first tab when switching phases */
  useEffect(() => {
    setActiveSubTab("collection_plan");
  }, [activePhase]);

  /** Track whether the chevron track can scroll in each direction */
  const [canScrollStart, setCanScrollStart] = useState(false);
  const [canScrollEnd, setCanScrollEnd] = useState(false);

  const updateScrollFlags = useCallback(() => {
    const el = chevronScrollRef.current;
    if (!el) return;
    const threshold = 2; // px tolerance for sub-pixel rounding
    setCanScrollStart(el.scrollLeft > threshold);
    setCanScrollEnd(el.scrollLeft + el.clientWidth < el.scrollWidth - threshold);
  }, []);

  // Re-check on mount, resize, and after phase changes
  useEffect(() => {
    updateScrollFlags();
    window.addEventListener("resize", updateScrollFlags);
    return () => window.removeEventListener("resize", updateScrollFlags);
  }, [updateScrollFlags]);

  // Also re-check whenever phases render (slight delay for layout)
  useEffect(() => {
    const timer = setTimeout(updateScrollFlags, 50);
    return () => clearTimeout(timer);
  }, [workspace?.phase, updateScrollFlags]);

  /** Scroll chevron strip left/right */
  const scrollChevrons = useCallback((direction: "start" | "end") => {
    const el = chevronScrollRef.current;
    if (!el) return;
    const amount = el.clientWidth * 0.6;
    el.scrollBy({
      left: direction === "end" ? amount : -amount,
      behavior: "smooth",
    });
  }, []);

  /** Phase status text below the name */
  const phaseStatusText = useCallback(
    (idx: number): string => {
      if (idx < currentPhaseIndex) return t("workspace.phaseStatus.completed");
      if (idx === currentPhaseIndex) return t("workspace.phaseStatus.inProgress");
      return t("workspace.phaseStatus.notStarted");
    },
    [currentPhaseIndex, t]
  );

  if (loading) {
    return <div className={styles.loading}>Loading…</div>;
  }

  if (error || !workspace) {
    return (
      <div className={styles.container}>
        <div className={styles.error}>{error ?? "Workspace not found."}</div>
      </div>
    );
  }

  const createdDate = new Date(workspace.createdAt).toLocaleDateString(
    undefined,
    { year: "numeric", month: "short", day: "numeric" }
  );

  // Prefer detail hero; fall back to card hero
  const heroKey = workspace.detailHeroImageUrl ?? workspace.heroImageUrl;
  const heroUrl = mediaSrc(heroKey);

  const selectedPhase = activePhase ?? workspace.phase;

  return (
    <div className={styles.container}>
      {/* ── Sticky header with red gradient ── */}
      <div className={styles.header}>
        {/* ── Title row: back + concern name + edit button ── */}
        <div className={styles.titleRow}>
          <div className={styles.concern}>
            {workspace.title || workspace.presentingConcern}
            {selectedPhase === "__summary" && <span className={styles.concernSuffix}> — {t("workspace.summary.title")}</span>}
          </div>
          <div className={styles.headerActions}>
            <button
              type="button"
              className={`${styles.headerSummaryBtn} ${selectedPhase === "__summary" ? styles.headerSummaryBtnActive : ""}`}
              onClick={() => setActivePhase("__summary")}
              title={t("workspace.summary.title")}
              aria-label={t("workspace.summary.title")}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
                <line x1="16" y1="13" x2="8" y2="13" />
                <line x1="16" y1="17" x2="8" y2="17" />
                <polyline points="10 9 9 9 8 9" />
              </svg>
              {t("workspace.summary.pill")}
            </button>
            <button
              type="button"
              className={styles.headerEditBtn}
              onClick={openEditModal}
              title={t("workspace.detail.editWorkspace")}
              aria-label={t("workspace.detail.editWorkspace")}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
              </svg>
            </button>
            <button
              type="button"
              className={styles.headerEditBtn}
              onClick={() => setTeamModalOpen(true)}
              title={t("workspace.detail.investigationTeam")}
              aria-label={t("workspace.detail.investigationTeam")}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                <path d="M16 3.13a4 4 0 0 1 0 7.75" />
              </svg>
            </button>
            {selectedPhase === "__summary" && (
              <button
                type="button"
                className={styles.headerEditBtn}
                onClick={() => window.print()}
                title={t("workspace.summary.printTitle")}
                aria-label={t("workspace.summary.printTitle")}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="6 9 6 2 18 2 18 9" />
                  <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
                  <rect x="6" y="14" width="12" height="8" />
                </svg>
              </button>
            )}
          </div>
        </div>

        {/* ── Chevron Phase Workflow ── */}
        <div className={styles.chevronRow}>
          {canScrollStart && (
            <button
              type="button"
              className={styles.chevronScrollBtn}
              onClick={() => scrollChevrons("start")}
              aria-label="Scroll phases left"
            >
              ‹
            </button>
          )}
          <div className={styles.chevronTrack} ref={chevronScrollRef} onScroll={updateScrollFlags}>
            {WORKSPACE_PHASES.map((phase, idx) => {
              const isCompleted = idx < currentPhaseIndex;
              const isActive = idx === currentPhaseIndex;
              const isSelected = phase === selectedPhase;
              const chevronClass = isCompleted
                ? styles.chevronCompleted
                : isActive
                ? styles.chevronActive
                : styles.chevronFuture;
              const selectedClass = isSelected ? styles.chevronSelected : "";

              return (
                <button
                  key={phase}
                  type="button"
                  className={`${styles.chevron} ${chevronClass} ${selectedClass}`}
                  onClick={() => setActivePhase(phase)}
                  title={t(`workspace.phaseDesc.${phase}`)}
                >
                  <span className={styles.chevronLabel}>
                    {isCompleted && (
                      <svg
                        className={styles.chevronCheck}
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="3"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    )}
                    <span className={styles.chevronName}>
                      {idx + 1}: {phaseLabel(phase)}
                    </span>
                  </span>
                  <span className={styles.chevronStatus}>
                    {phaseStatusText(idx)}
                  </span>
                </button>
              );
            })}
          </div>
          {canScrollEnd && (
            <button
              type="button"
              className={styles.chevronScrollBtn}
              onClick={() => scrollChevrons("end")}
              aria-label="Scroll phases right"
            >
              ›
            </button>
          )}
        </div>
      </div>

      {/* ── Phase body ── */}
      <div className={styles.body}>
        {/* Executive Summary — workspace-level, no co-pilot tray */}
        {selectedPhase === "__summary" && (
          <WorkspaceSummaryPanel
            workspace={workspace}
            sessions={aiData.sessions}
            onOpenTeamModal={() => setTeamModalOpen(true)}
            onWorkspaceUpdated={(updated) => setWorkspace(updated)}
          />
        )}

        {/* All non-summary phases get the co-pilot tray */}
        {selectedPhase && selectedPhase !== "__summary" && (
          <>
            {/* Detection Validation sub-tab bar */}
            {selectedPhase === "detection_validation" && (
              <div className={styles.subTabBar}>
                {DETECTION_SUB_TABS.map((tab) => (
                  <button
                    key={tab.key}
                    type="button"
                    className={
                      activeSubTab === tab.key
                        ? styles.subTabActive
                        : styles.subTab
                    }
                    onClick={() => setActiveSubTab(tab.key)}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            )}

            <div className={styles.phaseWithTray}>
              {/* Main content area */}
              <div className={styles.phaseMain}>

                {/* ── Detection Validation content ── */}
                {selectedPhase === "detection_validation" && (
                  <>
                    {activeSubTab === "collection_plan" && (
                      <EvidenceTracker
                        workspace={workspace}
                        evidenceItems={aiData.evidenceItems}
                        sessions={aiData.sessions}
                        onRefresh={aiData.refetch}
                        view="plan"
                      />
                    )}
                    {activeSubTab === "evidence" && (
                      <EvidenceTracker
                        workspace={workspace}
                        evidenceItems={aiData.evidenceItems}
                        sessions={aiData.sessions}
                        onRefresh={aiData.refetch}
                        view="evidence"
                      />
                    )}
                    {activeSubTab === "compilation" && (
                      <EvidenceCompilationPanel
                        workspace={workspace}
                        evidenceItems={aiData.evidenceItems}
                      />
                    )}
                  </>
                )}

                {/* ── Signal Detection ── */}
                {selectedPhase === "signal_detection" && (
                  <SignalDetectionPanel
                    workspace={workspace}
                    sessions={aiData.sessions}
                    onRefreshWorkspace={refreshWorkspace}
                  />
                )}

                {/* ── Analysis ── */}
                {selectedPhase === "analysis" && (
                  <div className={styles.panel}>
                    <div className={styles.phaseIntro}>
                      <h3 className={styles.phaseIntroTitle}>
                        {phaseLabel("analysis")}
                      </h3>
                      <p className={styles.phaseIntroDesc}>
                        {t("workspace.phaseDesc.analysis")}
                      </p>
                    </div>
                    <div className={styles.placeholder}>
                      <div>Causal analysis tools will be available once evidence is collected.</div>
                    </div>
                  </div>
                )}

                {/* ── Solution Design ── */}
                {selectedPhase === "solution_design" && (
                  <div className={styles.panel}>
                    <div className={styles.phaseIntro}>
                      <h3 className={styles.phaseIntroTitle}>
                        {phaseLabel("solution_design")}
                      </h3>
                      <p className={styles.phaseIntroDesc}>
                        {t("workspace.phaseDesc.solution_design")}
                      </p>
                    </div>
                    <div className={styles.placeholder}>
                      <div>{t("workspace.detail.noSolutions")}</div>
                      <button type="button" className={styles.placeholderBtn}>
                        {t("workspace.detail.addSolution")}
                      </button>
                    </div>
                  </div>
                )}

                {/* ── Resourcing ── */}
                {selectedPhase === "resourcing" && (
                  <div className={styles.panel}>
                    <div className={styles.phaseIntro}>
                      <h3 className={styles.phaseIntroTitle}>
                        {phaseLabel("resourcing")}
                      </h3>
                      <p className={styles.phaseIntroDesc}>
                        {t("workspace.phaseDesc.resourcing")}
                      </p>
                    </div>
                    <div className={styles.placeholder}>
                      <div>{t("workspace.phasePlaceholder.resourcing") || "Resource planning tools coming soon."}</div>
                    </div>
                  </div>
                )}

                {/* ── Implementation ── */}
                {selectedPhase === "implementation" && (
                  <div className={styles.panel}>
                    <div className={styles.phaseIntro}>
                      <h3 className={styles.phaseIntroTitle}>
                        {phaseLabel("implementation")}
                      </h3>
                      <p className={styles.phaseIntroDesc}>
                        {t("workspace.phaseDesc.implementation")}
                      </p>
                    </div>
                    <div className={styles.placeholder}>
                      <div>{t("workspace.phasePlaceholder.implementation") || "Implementation tracking tools coming soon."}</div>
                    </div>
                  </div>
                )}

                {/* ── Sustainment ── */}
                {selectedPhase === "sustainment" && (
                  <div className={styles.panel}>
                    <div className={styles.phaseIntro}>
                      <h3 className={styles.phaseIntroTitle}>
                        {phaseLabel("sustainment")}
                      </h3>
                      <p className={styles.phaseIntroDesc}>
                        {t("workspace.phaseDesc.sustainment")}
                      </p>
                    </div>
                    <div className={styles.placeholder}>
                      <div>{t("workspace.phasePlaceholder.sustainment") || "Long-term monitoring tools coming soon."}</div>
                    </div>
                  </div>
                )}
              </div>

              {/* Co-pilot tray (right edge) — visible on all phase tabs */}
              <CopilotTray
                workspace={workspace}
                onRefreshWorkspace={refreshWorkspace}
              />
            </div>
          </>
        )}
      </div>

      {/* ── Edit workspace modal ── */}
      {editModalOpen && (
        <div
          className={styles.modalBackdrop}
          onClick={(e) => {
            if (e.target === e.currentTarget && !editSaving) setEditModalOpen(false);
          }}
        >
          <div className={styles.editModal}>
            <div className={styles.modalHeader}>
              <span className={styles.modalTitle}>
                {t("workspace.detail.editWorkspace")}
              </span>
              <CloseButton onClick={() => !editSaving && setEditModalOpen(false)} size="md" />
            </div>
            <div className={styles.modalBody}>
              <div className={styles.editField}>
                <label className={styles.editLabel}>
                  {t("workspace.detail.descriptionLabel")}
                </label>
                <textarea
                  className={styles.editTextarea}
                  rows={5}
                  value={editDescription}
                  onChange={(e) => setEditDescription(e.target.value)}
                  placeholder={t("workspace.detail.descriptionPlaceholder")}
                  disabled={editSaving}
                />
              </div>
              <div className={styles.editField}>
                <label className={styles.editLabel}>
                  {t("workspace.detail.summaryFindingsLabel")}
                  <span className={styles.aiBadge}>AI + Human</span>
                </label>
                <textarea
                  className={styles.editTextarea}
                  rows={5}
                  value={editFindings}
                  onChange={(e) => setEditFindings(e.target.value)}
                  placeholder={t("workspace.detail.summaryFindingsPlaceholder")}
                  disabled={editSaving}
                />
              </div>

              {/* ── Danger zone: soft-delete (lead/sysadmin) ── */}
              {canDelete && (
                <div style={{ borderBlockStart: "1px solid var(--color-error)", paddingBlockStart: 14, marginBlockStart: 6 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: "var(--color-error)", marginBlockEnd: 4 }}>
                    {t("workspace.detail.dangerHeading")}
                  </div>
                  <div style={{ fontSize: 12, color: "var(--text-muted)", lineHeight: 1.5, marginBlockEnd: 10 }}>
                    {t("workspace.detail.dangerHelp")}
                  </div>
                  {!confirmDelete ? (
                    <button
                      type="button"
                      onClick={() => { setConfirmDelete(true); setDelText(""); }}
                      style={{ padding: "8px 14px", borderRadius: 8, border: "1px solid var(--color-error)", background: "transparent", color: "var(--color-error)", fontWeight: 600, fontSize: 13, cursor: "pointer" }}
                    >
                      {t("workspace.detail.deleteButton")}
                    </button>
                  ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                      <label style={{ fontSize: 12, color: "var(--text-tertiary)" }}>
                        {t("workspace.detail.deleteConfirmLabel", { name: workspace.title || workspace.presentingConcern })}
                      </label>
                      <input
                        className={styles.editTextarea}
                        style={{ minHeight: 0 }}
                        value={delText}
                        onChange={(e) => setDelText(e.target.value)}
                        placeholder={workspace.title || workspace.presentingConcern}
                        autoFocus
                      />
                      <div style={{ display: "flex", gap: 8 }}>
                        <button
                          type="button"
                          onClick={() => { setConfirmDelete(false); setDelText(""); }}
                          disabled={deleting}
                          style={{ padding: "8px 14px", borderRadius: 8, border: "1px solid var(--border-default)", background: "transparent", color: "var(--text-secondary)", fontWeight: 600, fontSize: 13, cursor: "pointer" }}
                        >
                          {t("workspace.cancel")}
                        </button>
                        <button
                          type="button"
                          onClick={handleSoftDelete}
                          disabled={deleting || delText.trim() !== (workspace.title || workspace.presentingConcern).trim()}
                          style={{
                            padding: "8px 14px", borderRadius: 8, border: 0, background: "var(--color-error)", color: "#fff", fontWeight: 700, fontSize: 13,
                            cursor: (deleting || delText.trim() !== (workspace.title || workspace.presentingConcern).trim()) ? "default" : "pointer",
                            opacity: (deleting || delText.trim() !== (workspace.title || workspace.presentingConcern).trim()) ? 0.4 : 1,
                          }}
                        >
                          {deleting ? t("workspace.detail.deleting") : t("workspace.detail.deleteConfirmButton")}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className={styles.modalFooter}>
              <button
                type="button"
                className={styles.modalSecondaryBtn}
                onClick={() => setEditModalOpen(false)}
                disabled={editSaving}
              >
                {t("workspace.cancel")}
              </button>
              <button
                type="button"
                className={styles.modalPrimaryBtn}
                onClick={handleEditSave}
                disabled={editSaving}
              >
                {editSaving
                  ? t("workspace.detail.saving")
                  : t("workspace.detail.save")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Investigation Team modal ── */}
      {teamModalOpen && (
        <InvestigationTeamModal
          workspaceGuid={workspace.workspaceGuid}
          teamLeadGuid={workspace.teamLeadGuid}
          onClose={() => setTeamModalOpen(false)}
          onTeamChanged={refreshWorkspace}
        />
      )}
    </div>
  );
};

export default WorkspaceDetailPage;
